/**
 * Generic Sidebar Component
 * =========================
 * 
 * A reusable sidebar navigation component used across all dashboard layouts.
 * Provides consistent navigation experience for Admin, User, and other roles.
 * 
 * Features:
 * - Configurable navigation items
 * - Collapsible/expandable sidebar
 * - User info display at bottom
 * - Logout button
 * - Active route highlighting
 * - Nested navigation support (dropdown menus)
 * - Responsive design
 * 
 * Props:
 * - logo: Logo text or component (e.g., "Gordian", "Admin Panel")
 * - navItems: Array of navigation items with labels, icons, routes
 * - currentRoute: Currently active route
 * - onNavigate: Navigation handler
 * - user: User object (name, email, avatar)
 * - onLogout: Logout handler
 * - variant: Sidebar color variant ('admin', 'user', 'custom')
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import './Sidebar.scss';

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  route?: string;  // Optional for items with subItems
  description?: string;
  badge?: number;  // Optional badge count (e.g., cart items)
  badgeVariant?: 'default' | 'mentioned';  // Badge color variant
  children?: NavItem[];  // For nested navigation
  subItems?: Array<{  // For dropdown menu items
    id: string;
    label: string;
    route: string;
    description?: string;
  }>;
}

interface SidebarProps {
  /** Logo text or component displayed at top */
  logo: string | React.ReactNode;
  /** Optional logo URL for company logos */
  logoUrl?: string;
  /** Array of navigation items */
  navItems: NavItem[];
  /** Currently active route */
  currentRoute: string;
  /** Navigation handler */
  onNavigate: (route: string) => void;
  /** User information */
  user: {
    name: string;
    email: string;
    avatar?: string;
  };
  /** Logout handler */
  onLogout: () => void;
  /** Sidebar color variant (default: 'admin') */
  variant?: 'admin' | 'user' | 'custom';
  /** Custom gradient colors (only used if variant='custom') */
  customGradient?: {
    from: string;
    to: string;
  };
  /** Cart button props */
  cartButton?: {
    label: string;
    onClick: () => void;
    className?: string;
  };
  /** Secondary sidebar component (e.g., ProductOverviewSidebar) */
  secondarySidebar?: React.ReactNode;
  /** Optional style overrides */
  style?: React.CSSProperties;
}

const Sidebar: React.FC<SidebarProps> = ({
  logo,
  logoUrl,
  navItems,
  currentRoute,
  onNavigate,
  user,
  onLogout,
  variant = 'admin',
  customGradient,
  cartButton,
  secondarySidebar,
  style
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());

  // Keep dropdowns open when navigating to their child routes
  useEffect(() => {
    // Find which dropdown should stay open based on current route
    const dropdownsToKeepOpen = new Set<string>();

    // Check each navigation item with children
    navItems.forEach(item => {
      if (item.children && item.children.length > 0) {
        // Check if current route matches any child route
        const hasMatchingChild = item.children.some(child => {
          if (!child.route) return false;
          const childPath = child.route.split('?')[0]; // Remove query params
          const currentPath = currentRoute.split('?')[0];
          // Match exact route
          if (currentPath === childPath) {
            return true;
          }
          // Match if current route starts with child path followed by / (for nested routes)
          // e.g., /dashboard/user/rfq/xxx/quotes-review matches /dashboard/user/rfq/xxx/quotes-review
          if (currentPath.startsWith(childPath + '/')) {
            return true;
          }
          // Also check if current route is the child path itself (for routes without trailing slash)
          // This handles cases where child route is a prefix of current route
          // e.g., /dashboard/user/rfq/xxx/quotes-review should match child /dashboard/user/rfq/xxx/quotes-review
          if (childPath.length > 0 && currentPath.indexOf(childPath) === 0) {
            // Make sure it's not just a partial match (e.g., /dashboard/user/rfq matching /dashboard/user/rfq/xxx)
            const nextChar = currentPath[childPath.length];
            if (!nextChar || nextChar === '/' || nextChar === '?') {
              return true;
            }
          }

          return false;
        });

        if (hasMatchingChild) {
          dropdownsToKeepOpen.add(item.id);
        }
      }
    });

    // Always update dropdowns state to keep matching ones open
    // This ensures dropdowns stay open even when route changes
    setOpenDropdowns(prev => {
      const newSet = new Set(prev);
      // Keep all dropdowns that should be open
      dropdownsToKeepOpen.forEach(id => newSet.add(id));
      // Don't remove dropdowns that are already open and have matching children
      // This prevents closing dropdowns when navigating between child routes
      return newSet;
    });
  }, [currentRoute, navItems]);

  // Toggle dropdown menu
  const toggleDropdown = (itemId: string) => {
    setOpenDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Check if route is active (including nested routes)
  const isRouteActive = (route: string | undefined, item: NavItem): boolean => {
    const currentPath = currentRoute.split('?')[0]; // Remove query params

    // Direct match
    if (route) {
      const routePath = route.split('?')[0];
      if (currentPath === routePath) return true;
    }

    // Special case: treat /dashboard/user/create-request/manual as matching /dashboard/user/create-request/marketplace
    if (route === '/dashboard/user/create-request/marketplace') {
      if (currentPath.startsWith('/dashboard/user/create-request/')) return true;
    }

    // Check if current route starts with the item route (for nested routes)
    // e.g., /dashboard/user/approve-rfq/123 matches /dashboard/user/approve-rfq
    if (route) {
      const routePath = route.split('?')[0];
      if (currentPath.startsWith(routePath + '/')) return true;
    }

    // Check if any child is active
    if (item.children) {
      const hasActiveChild = item.children.some(child => {
        if (!child.route) return false;
        const childPath = child.route.split('?')[0];
        if (currentPath === childPath) return true;
        if (currentPath.startsWith(childPath + '/')) return true;
        return false;
      });

      // If we have an active child, only mark parent as active if we're NOT on the parent route itself
      if (hasActiveChild) {
        if (route) {
          const routePath = route.split('?')[0];
          // If current path is exactly the parent route, don't mark parent as active
          if (currentPath === routePath) return false;
        }
        // If we're on a child route, don't mark parent as active
        return false;
      }
    }

    // Check if any subItem is active
    if (item.subItems) {
      return item.subItems.some(subItem => {
        if (!subItem.route) return false;
        const subItemPath = subItem.route.split('?')[0];
        if (currentPath === subItemPath) return true;
        if (currentPath.startsWith(subItemPath + '/')) return true;
        return false;
      });
    }

    return false;
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return parts[0][0] + parts[1][0];
      }
      return user.name.charAt(0);
    }
    return 'U';
  };

  // Render navigation item
  const renderNavItem = (item: NavItem, isChild = false) => {
    // Handle separator items (empty label and no route)
    if (!item.label && !item.route) {
      return <div key={item.id} className="sidebar-v2__nav-separator" />;
    }

    const hasChildren = item.children && item.children.length > 0;
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isDropdownOpen = openDropdowns.has(item.id);

    // For child items, check if the child route matches current route
    let isActive = false;
    if (isChild && item.route) {
      const childPath = item.route.split('?')[0];
      const currentPath = currentRoute.split('?')[0];
      // Exact match only - don't use startsWith for child items to prevent parent routes from being active
      // when on child routes (e.g., RFQ Details should not be active when on Communication page)
      isActive = currentPath === childPath;
    } else {
      isActive = isRouteActive(item.route, item);
    }

    return (
      <div key={item.id} className={`sidebar-v2__nav-item-wrapper ${isChild ? 'child' : ''}`}>
        <button
          className={`sidebar-v2__nav-item ${isActive ? 'active' : ''} ${isChild ? 'child-item' : ''}`}
          onClick={(e) => {
            // For child items, only navigate, don't toggle parent dropdown
            if (isChild) {
              if (item.route) {
                onNavigate(item.route);
              }
              return;
            }

            // If item has children/subItems
            if (hasChildren || hasSubItems) {
              // If route exists, navigate and ensure dropdown is OPEN (don't toggle close)
              if (item.route && item.route.trim() !== '') {
                onNavigate(item.route);
                if (!isDropdownOpen) {
                  toggleDropdown(item.id);
                }
              } else {
                // No route, standard toggle behavior
                toggleDropdown(item.id);
              }
            } else {
              // If no children, navigate to route if it exists
              if (item.route) {
                onNavigate(item.route);
              }
            }
          }}
          title={isCollapsed ? item.label : item.description}
        >
          <span className="sidebar-v2__nav-icon">{item.icon}</span>
          {!isCollapsed && (
            <>
              <span className="sidebar-v2__nav-label">{item.label}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <span className={`sidebar-v2__nav-badge ${item.badgeVariant === 'mentioned' ? 'sidebar-v2__nav-badge--mentioned' : ''}`}>
                  {item.badge}
                </span>
              )}
              {/* Dropdown icon removed as requested
              {(hasChildren || hasSubItems) && (
                <span className="sidebar-v2__nav-dropdown-icon">
                  {isDropdownOpen ? '▼' : '▶'}
                </span>
              )}
              */}
            </>
          )}
        </button>

        {/* Render children if dropdown is open */}
        {hasChildren && isDropdownOpen && !isCollapsed && (
          <div className="sidebar-v2__nav-children">
            {item.children!.map(child => renderNavItem(child, true))}
          </div>
        )}

        {/* Render subItems if dropdown is open */}
        {hasSubItems && isDropdownOpen && !isCollapsed && (
          <div className="sidebar-v2__nav-children">
            {item.subItems!.map(subItem => (
              <button
                key={subItem.id}
                className={`sidebar-v2__nav-item child-item ${currentRoute === subItem.route ? 'active' : ''}`}
                onClick={() => onNavigate(subItem.route)}
                title={subItem.description}
              >
                <span className="sidebar-v2__nav-icon">•</span>
                <span className="sidebar-v2__nav-label">{subItem.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`sidebar-container ${secondarySidebar ? 'sidebar-container--dual' : ''}`}
      style={style}
    >
      {/* Main Sidebar */}
      <aside className={`sidebar-v2 sidebar-v2--${variant} ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Logo/Header Section */}
        <div className="sidebar-v2__header">
          <div className="sidebar-v2__logo">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={typeof logo === 'string' ? logo : 'Company Logo'}
                className="sidebar-v2__logo-image"
              />
            )}
            {typeof logo === 'string' ? (
              <span className={logoUrl ? 'sidebar-v2__logo-text' : ''}>
                {isCollapsed ? logo.charAt(0) : logo}
              </span>
            ) : (
              logo
            )}
          </div>
          <button
            className="sidebar-v2__toggle"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? '→' : '←'}
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-v2__nav">
          {navItems.map(item => renderNavItem(item))}
        </nav>

        {/* Cart Button */}
        {cartButton && (
          <div className="sidebar-v2__cart-section">
            <button
              className={`sidebar-v2__cart-btn ${cartButton.className || ''}`}
              onClick={cartButton.onClick}
              title="Shopping Cart"
            >
              {cartButton.label}
            </button>
          </div>
        )}

        {/* User Info at Bottom */}
        <div className="sidebar-v2__footer">
          <div className="sidebar-v2__user-info">
            <div className="sidebar-v2__user-avatar">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} />
              ) : (
                <span>{getUserInitials()}</span>
              )}
            </div>
            {!isCollapsed && (
              <div className="sidebar-v2__user-details">
                <div className="sidebar-v2__user-name">{user.name}</div>
                <div className="sidebar-v2__user-email">{user.email}</div>
              </div>
            )}
          </div>
          <button
            className="sidebar-v2__logout-btn"
            onClick={onLogout}
            title="Logout"
          >
            {isCollapsed ? '🚪' : 'Logout'}
          </button>
        </div>
      </aside>

      {/* Secondary Sidebar */}
      {secondarySidebar && (
        <div className="sidebar-v2__secondary">
          {secondarySidebar}
        </div>
      )}
    </div>
  );
};

export default Sidebar;

